// Step one of the CLI sign-in approval: type the code the terminal printed.
//
// Typing is the whole point. The member transcribes a code they can see on
// their own screen, which is the step a link cannot perform on their behalf,
// so this form must never be pre-filled from anywhere. See the note at the top
// of index.tsx.

import { Box, Text } from "@chakra-ui/react";
import { FormEvent, forwardRef } from "react";
import { TbArrowRight } from "react-icons/tb";

import Input from "components/ds/Input";
import PloyButton from "components/ds/PloyButton";

export type CodeFormProps = {
  code: string;
  onCodeChange: (next: string) => void;
  onSubmit: () => void;
  isLoading: boolean;
  error: string | null;
};

const HELP_ID = "cli-code-help";
const ERROR_ID = "cli-code-error";
const INPUT_ID = "cli-code-input";

const CodeForm = forwardRef<HTMLInputElement, CodeFormProps>(
  ({ code, onCodeChange, onSubmit, isLoading, error }, ref) => {
    const submit = (e: FormEvent) => {
      e.preventDefault();
      onSubmit();
    };

    return (
      <Box as="form" onSubmit={submit} w="full">
        {/* A real <label> rather than the ds FieldLabel, which renders a span
            and so cannot carry htmlFor. Same tokens, so it looks identical. */}
        <Text
          as="label"
          htmlFor={INPUT_ID}
          display="block"
          fontSize="xs"
          fontWeight={500}
          color="neutral.800"
          mb="1xs"
        >
          Code from your terminal
        </Text>

        <Input
          id={INPUT_ID}
          ref={ref}
          value={code}
          onChange={(e) => onCodeChange(e.target.value)}
          placeholder="XXXX-XXXX"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="characters"
          spellCheck={false}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${ERROR_ID} ${HELP_ID}` : HELP_ID}
          fontFamily="mono"
          letterSpacing="0.12em"
          fontSize="xl"
          py="md"
        />

        <Text id={HELP_ID} fontSize="xs" color="neutral.700" mt="1xs">
          Eight characters. Capitals and the hyphen are optional.
        </Text>

        {error && (
          <Text
            id={ERROR_ID}
            role="alert"
            fontSize="smd"
            fontWeight={500}
            color="error.700"
            mt="sm"
          >
            {error}
          </Text>
        )}

        <PloyButton
          type="submit"
          variant="primary"
          size="md"
          mt="lg"
          w="full"
          isLoading={isLoading}
          loadingText="Checking"
          rightIcon={<TbArrowRight size={16} aria-hidden />}
        >
          Continue
        </PloyButton>
      </Box>
    );
  },
);

CodeForm.displayName = "CodeForm";

export default CodeForm;
